import { RefreshCw } from "lucide-react-native";
import { FC } from "react";

import { useUseCaseLabels } from "@/components/studies/useCaseRequirements";
import { HStack, Text, VStack } from "@/components/ui";
import { Section } from "@/types/resources";
import { useSectionResults } from "./SectionResults";

interface SectionHeaderProps {
  section: Section;
}

/**
 * Presentational section header for run results: the section title plus, in
 * report mode only, a read-only rotation summary ("2 packs per respondent",
 * etc.). Per-cell variable values are surfaced inside the substituted question
 * text in the detailed view (`QuestionHeader`), not here — the composite report
 * isn't question-by-question, so there's no per-cell text to show.
 */
const SectionHeader: FC<SectionHeaderProps> = ({ section }) => {
  const labels = useUseCaseLabels(section.use_case);
  const { detailed } = useSectionResults();

  // In the detailed view the cell tab bar already conveys the rotation, so the
  // summary is only meaningful for the composite report.
  const configParts: string[] = [];
  if (!detailed) {
    const cells = section.config?.cells_per_respondent;
    if (typeof cells === "number" && cells >= 1) {
      configParts.push(labels.summaryCells(cells));
    }
    const items = section.config?.items_per_cell;
    if (typeof items === "number" && items > 1) {
      configParts.push(labels.summaryItems(items));
    }
  }

  return (
    <VStack className="gap-1">
      <Text className="text-base font-semibold text-typography-900 leading-relaxed">
        {section.title}
      </Text>

      {configParts.length > 0 && (
        <HStack className="items-center gap-1 mt-1">
          <RefreshCw color="rgb(var(--color-typography-400))" size={11} />
          <Text className="text-xs text-typography-400">
            {configParts.join(" · ")}
          </Text>
        </HStack>
      )}
    </VStack>
  );
};

export default SectionHeader;
